import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, Users, Briefcase, Factory, Package, Handshake, Trophy, Menu,
  LogOut, LayoutDashboard, Building, Info, Bell, FileText, Quote, UserPlus
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// Import CMS panels
import CompanyInfoPanel from '../components/cms/CompanyInfoPanel';
import TeamMembersPanel from '../components/cms/TeamMembersPanel';
import ServicesPanel from '../components/cms/ServicesPanel';
import IndustriesPanel from '../components/cms/IndustriesPanel';
import ProductsPanel from '../components/cms/ProductsPanel';
import PartnersPanel from '../components/cms/PartnersPanel';
import SuccessStoriesPanel from '../components/cms/SuccessStoriesPanel';
import ServiceMenuPanel from '../components/cms/ServiceMenuPanel';
import ServicesPagePanel from '../components/cms/ServicesPagePanel';
import IndustryMenuPanel from '../components/cms/IndustryMenuPanel';
import AboutPagePanel from '../components/cms/AboutPagePanel';
import NotificationRecipientsPanel from '../components/cms/NotificationRecipientsPanel';
import BlogPanel from '../components/cms/BlogPanel';
import TestimonialsPanel from '../components/cms/TestimonialsPanel';
import CareersPanel from '../components/cms/CareersPanel';

const CMSDashboard = () => {
  const [activeTab, setActiveTab] = useState('company');
  const { user, logout } = useAuth();

  const tabs = [
    { id: 'company', label: 'Company Info', icon: Building2 },
    { id: 'about-page', label: 'About Page', icon: Info },
    { id: 'blog', label: 'Blog Posts', icon: FileText },
    { id: 'testimonials', label: 'Testimonials', icon: Quote },
    { id: 'careers', label: 'Careers', icon: UserPlus },
    { id: 'service-menu', label: 'Service Menu', icon: Menu },
    { id: 'services-page', label: 'Services Page', icon: LayoutDashboard },
    { id: 'industry-menu', label: 'Industry Menu', icon: Building },
    { id: 'team', label: 'Team Members', icon: Users },
    { id: 'services', label: 'Services', icon: Briefcase },
    { id: 'industries', label: 'Industries', icon: Factory },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'partners', label: 'Partners', icon: Handshake },
    { id: 'success-stories', label: 'Success Stories', icon: Trophy },
    { id: 'notifications', label: 'Email Notifications', icon: Bell },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'company':
        return <CompanyInfoPanel />;
      case 'about-page':
        return <AboutPagePanel token={user?.token} />;
      case 'blog':
        return <BlogPanel />;
      case 'testimonials':
        return <TestimonialsPanel />;
      case 'careers':
        return <CareersPanel token={user?.token} />;
      case 'service-menu':
        return <ServiceMenuPanel />;
      case 'services-page':
        return <ServicesPagePanel />;
      case 'industry-menu':
        return <IndustryMenuPanel />;
      case 'team':
        return <TeamMembersPanel />;
      case 'services':
        return <ServicesPanel />;
      case 'industries':
        return <IndustriesPanel />;
      case 'products':
        return <ProductsPanel />;
      case 'partners':
        return <PartnersPanel />;
      case 'success-stories':
        return <SuccessStoriesPanel />;
      case 'notifications':
        return <NotificationRecipientsPanel />;
      default:
        return <CompanyInfoPanel />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_mobittech/artifacts/hyzx3sw9_Frame%202%20%281%29.png" 
                alt="Navaris Digital CMS" 
                className="h-8 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-[#320547]">CMS</h1>
                <p className="text-xs text-gray-500">Content Management System</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Welcome, {user?.username}</span>
              <Button
                onClick={logout}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                data-testid="cms-logout-btn"
              >
                <LogOut size={16} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-12 lg:col-span-3">
            <Card>
              <CardContent className="p-4">
                <nav className="space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        data-testid={`cms-tab-${tab.id}`}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          activeTab === tab.id
                            ? 'bg-[#320547] text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Icon size={20} />
                        <span className="font-medium">{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Content Area */}
          <div className="col-span-12 lg:col-span-9">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderContent()}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMSDashboard;
